import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  HoverCard, HoverCardContent, HoverCardTrigger,
} from "@/components/ui/hover-card";
import api from "@/lib/api";
import { TrendingUp, Sparkles, BadgeCheck, ArrowUpRight } from "lucide-react";

// One-shot cache — the stats payload is small and the same across all
// sidebar items, so we fetch once and share it across every hover card.
let __statsCache = null;
let __statsPromise = null;
const listeners = new Set();

async function loadStats() {
  if (__statsCache) return __statsCache;
  if (!__statsPromise) {
    __statsPromise = api
      .get("/dashboard/sidebar-stats")
      .then((r) => {
        __statsCache = r.data;
        listeners.forEach((l) => l(__statsCache));
        return __statsCache;
      })
      .catch(() => {
        __statsCache = null;
        __statsPromise = null;
      });
  }
  return __statsPromise;
}

function useStats() {
  const [stats, setStats] = useState(__statsCache);
  useEffect(() => {
    let mounted = true;
    if (!__statsCache) loadStats();
    const onUpdate = (s) => { if (mounted) setStats(s); };
    listeners.add(onUpdate);
    return () => {
      mounted = false;
      listeners.delete(onUpdate);
    };
  }, []);
  return stats;
}

function ProgressBar({ pct, tone = "bg-blue-500" }) {
  const width = Math.max(0, Math.min(100, pct || 0));
  return (
    <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${tone}`}
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

function StatChip({ label, value, tone = "text-slate-900" }) {
  return (
    <div className="bg-slate-50 rounded-md px-2 py-1.5 flex flex-col">
      <span className="text-[9px] uppercase tracking-widest font-semibold text-slate-500">{label}</span>
      <span className={`font-mono font-bold text-sm mt-0.5 tabular-nums ${tone}`}>{value}</span>
    </div>
  );
}

function PreviewContent({ id, stats, t }) {
  if (!stats) {
    return <div className="text-xs text-slate-400 py-2 text-center">{t("sidebarPreview.loading")}</div>;
  }
  // Webinars is a static promo (no stats). Rendered first so it doesn't
  // fall through the `stats[id]` guard below.
  if (id === "webinars") {
    return (
      <>
        <p className="text-xs text-slate-600 leading-relaxed">
          {t("sidebarPreview.webinars.desc")}
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <StatChip label={t("sidebarPreview.webinars.format")}  value={t("sidebarPreview.webinars.formatValue")} />
          <StatChip label={t("sidebarPreview.webinars.filters")} value={t("sidebarPreview.webinars.filtersValue")} />
        </div>
        <div className="mt-2 text-[11px] text-slate-500">
          {t("sidebarPreview.webinars.joinLive")}
        </div>
      </>
    );
  }
  const s = stats[id];
  if (!s) return null;

  switch (id) {
    case "overview": {
      const total = stats.overview.total_lessons || 0;
      const done = stats.overview.completed_lessons || 0;
      const pct = total ? Math.round((done / total) * 100) : 0;
      return (
        <>
          <p className="text-xs text-slate-600 leading-relaxed">{t("sidebarPreview.overview.desc")}</p>
          <div className="mt-3 flex items-center justify-between text-xs mb-1">
            <span className="font-semibold text-slate-700">{t("sidebarPreview.overview.progress")}</span>
            <span className="font-mono font-bold text-slate-900">{pct}%</span>
          </div>
          <ProgressBar pct={pct} tone="bg-gradient-to-r from-blue-500 to-amber-400" />
          <div className="mt-2 text-[11px] text-slate-500">{t("sidebarPreview.overview.complete", { done, total })}</div>
        </>
      );
    }

    case "courses": {
      const c = stats.courses;
      return (
        <>
          <p className="text-xs text-slate-600 leading-relaxed">
            {t("sidebarPreview.courses.desc", { count: c.course_count })}
          </p>
          <div className="mt-3 flex items-center justify-between text-xs mb-1">
            <span className="font-semibold text-slate-700">{t("sidebarPreview.courses.curriculum")}</span>
            <span className="font-mono font-bold text-slate-900">{c.completed_lessons}/{c.total_lessons}</span>
          </div>
          <ProgressBar pct={c.progress_pct} tone="bg-blue-500" />
          <div className="mt-2 text-[11px] text-slate-500">
            {t("sidebarPreview.courses.note")}
          </div>
        </>
      );
    }

    case "articles": {
      const a = stats.articles;
      return (
        <>
          <p className="text-xs text-slate-600 leading-relaxed">{t("sidebarPreview.articles.desc")}</p>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <StatChip label={t("sidebarPreview.articles.total")}  value={a.available} />
            <StatChip label={t("sidebarPreview.articles.read")}   value={a.read}   tone="text-emerald-600" />
            <StatChip label={t("sidebarPreview.articles.unread")} value={a.unread} tone="text-amber-600" />
          </div>
        </>
      );
    }

    case "resources": {
      const r = stats.resources;
      return (
        <>
          <p className="text-xs text-slate-600 leading-relaxed">{t("sidebarPreview.resources.desc")}</p>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <StatChip label={t("sidebarPreview.resources.journal30d")} value={r.journal_entries_30d} />
            <StatChip label={t("sidebarPreview.resources.climb")}      value={r.climb_plans} />
            <StatChip label={t("sidebarPreview.resources.graded30d")}  value={r.graded_30d} tone="text-blue-700" />
          </div>
          {r.journal_entries_7d > 0 && (
            <div className="mt-2 text-[11px] text-emerald-600 font-semibold flex items-center gap-1">
              <TrendingUp size={10} /> {t("sidebarPreview.resources.entriesThisWeek", { count: r.journal_entries_7d })}
            </div>
          )}
        </>
      );
    }

    case "professor": {
      const p = stats.professor;
      return (
        <>
          <p className="text-xs text-slate-600 leading-relaxed">{t("sidebarPreview.professor.desc")}</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <StatChip label={t("sidebarPreview.professor.conversations")} value={p.conversations} />
            <StatChip label={t("sidebarPreview.professor.messages30d")}   value={p.messages_30d} />
          </div>
          {p.messages_30d === 0 && (
            <div className="mt-2 text-[11px] text-blue-700 font-semibold flex items-center gap-1">
              <Sparkles size={10} /> {t("sidebarPreview.professor.askFirst")}
            </div>
          )}
        </>
      );
    }

    case "certificates": {
      const c = stats.certificates;
      const pct = c.total ? Math.round((c.earned / c.total) * 100) : 0;
      return (
        <>
          <p className="text-xs text-slate-600 leading-relaxed">{t("sidebarPreview.certificates.desc")}</p>
          <div className="mt-3 flex items-center justify-between text-xs mb-1">
            <span className="font-semibold text-slate-700">{t("sidebarPreview.certificates.progress")}</span>
            <span className="font-mono font-bold text-slate-900">{c.earned}/{c.total}</span>
          </div>
          <ProgressBar pct={pct} tone={c.has_graduate ? "bg-emerald-500" : "bg-gradient-to-r from-blue-500 to-amber-400"} />
          {c.has_graduate ? (
            <div className="mt-2 text-[11px] text-emerald-600 font-semibold flex items-center gap-1">
              <BadgeCheck size={11} /> {t("sidebarPreview.certificates.graduate")}
            </div>
          ) : (
            <div className="mt-2 text-[11px] text-slate-500">
              {t("sidebarPreview.certificates.remaining", { count: c.total - c.earned })}
            </div>
          )}
        </>
      );
    }

    case "shop": {
      return (
        <>
          <p className="text-xs text-slate-600 leading-relaxed">{t("sidebarPreview.shop.desc")}</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <StatChip label={t("sidebarPreview.shop.available")} value={stats.shop.items_available} />
            <StatChip label={t("sidebarPreview.shop.yourTier")}  value={<span className="capitalize">{t(`tier.${stats.account.tier}`)}</span>} />
          </div>
        </>
      );
    }

    case "account": {
      const a = stats.account;
      return (
        <>
          <p className="text-xs text-slate-600 leading-relaxed">{t("sidebarPreview.account.desc")}</p>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <StatChip label={t("sidebarPreview.account.tier")}     value={<span className="capitalize">{t(`tier.${a.tier}`)}</span>} />
            <StatChip label={t("sidebarPreview.account.language")} value={a.language.toUpperCase()} />
            <StatChip label={t("sidebarPreview.account.refs")}     value={`${a.referrals_activated}/3`} tone="text-amber-600" />
          </div>
        </>
      );
    }

    default:
      return null;
  }
}

// Which sections are locked behind a paid tier, per current membership.
// If the section id appears in this map for the user's tier, we show an
// amber "Members Only" (or "Premium Only") pill in the preview header.
const UPGRADE_GATES = {
  free: {
    webinars:     { pillKey: "membersOnly",       target: "/pricing?src=hover-webinars" },
    resources:    { pillKey: "membersOnly",       target: "/pricing?src=hover-resources" },
    professor:    { pillKey: "membersOnly",       target: "/pricing?src=hover-professor" },
    certificates: { pillKey: "membersOnly",       target: "/pricing?src=hover-certificates" },
    shop:         { pillKey: "membersOnly",       target: "/pricing?src=hover-shop" },
  },
  basic: {
    resources: { pillKey: "premiumVault",      target: "/pricing?src=hover-resources-basic" },
    professor: { pillKey: "unlimitedPremium",  target: "/pricing?src=hover-professor-basic" },
  },
  premium: {},
  admin: {},
};

const VALID_IDS = new Set([
  "overview", "courses", "articles", "webinars", "resources",
  "professor", "certificates", "shop", "account",
]);

export function SidebarPreview({ id, children }) {
  const { t } = useTranslation();
  const stats = useStats();
  const tier = stats?.account?.tier || "free";
  const gate = UPGRADE_GATES[tier]?.[id];
  // If we don't have a titled preview for this id, render the child as-is —
  // keeps every sidebar row DOM-identical.
  if (!id || !VALID_IDS.has(id)) return children;

  return (
    <HoverCard openDelay={1000} closeDelay={120}>
      <HoverCardTrigger asChild>
        {children}
      </HoverCardTrigger>
      <HoverCardContent
        side="right"
        align="start"
        sideOffset={12}
        className="w-72 p-4 rounded-xl border border-slate-200 shadow-lg bg-white z-[100]"
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
          <div className="text-xs uppercase tracking-widest font-bold text-slate-800">
            {t(`sidebarPreview.titles.${id}`)}
          </div>
          {gate && (
            <span className="ml-auto inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
              <Sparkles size={8} /> {t(`sidebarPreview.gates.${gate.pillKey}`)}
            </span>
          )}
          {!gate && <ArrowUpRight size={11} className="ml-auto text-slate-400" />}
        </div>
        <PreviewContent id={id} stats={stats} t={t} />
        {gate && (
          <a
            href={gate.target}
            className="mt-4 flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-bold uppercase tracking-widest transition-colors"
          >
            {t("sidebarPreview.upgradeToUnlock")}
            <ArrowUpRight size={12} />
          </a>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

// Exposed so callers (e.g. code that just added a journal entry) can force
// the sidebar previews to refresh. Not wired in yet — future enhancement.
export function invalidateSidebarStats() {
  __statsCache = null;
  __statsPromise = null;
}
